<template>
  <div
    class="sub-accounts-table">

    <el-dialog
      @close="onClose()"
      width-size="x-large"
      height-size="large"
      :title="$t('wre-dashboard.sub-accounts-dialog-box.title')"
      visible>
      <div class="mx-24 my-16">
        <el-search
          style="width: 264px;"
          :placeholder="$t('wre-dashboard.sub-accounts-dialog-box.search.input.hint')"
          v-model="searchInputValue"/>
      </div>

      <el-table
        style="height: 100%;"
        height="fit-scrollable"
        :empty-text="searchInputValue !== '' ? $t('wre-dashboard.sub-accounts-dialog-box.table.message.empty') : ''"
        :data="tableData"
        @row-click="onTableRowClick"
        :default-sort = "{ prop: 'displayName' }"
        :loading-append="isFetchSubAccountItemsInProgress"
        highlight-current-row
        ref="sbTable"
        border-bottom>

        <el-table-column
          prop="id"
          hidden/>

        <el-table-column
          prop="space.used"
          hidden/>

        <el-table-column
          prop="space.total"
          hidden/>

        <el-table-column
          prop="volumeSubscription"
          hidden/>

        <el-table-column
          prop="displayName"
          :label="$t('wre-dashboard.sub-accounts-dialog-box.table.column.name')"
          :sort-by="row => row.displayName"
          min-width="110"
          align="left"
          sortable>
          <template slot-scope="{ row }">

            <div style="display: inline-block; padding-top: 8px;">
              <el-icon
                :name="row.volumeSubscription === true ? 'svg-devices-clr--32' : 'svg-pc-clr--32'"
              />
            </div>

            <span
              class="ml-16"
              v-highlight="searchInputValue">
              {{ row.displayName }}
            </span>
          </template>
        </el-table-column>

        <el-table-column
          prop="usage"
          :label="$t('wre-dashboard.sub-accounts-dialog-box.table.column.usage')"
          :sort-by="row => row.space.used"
          sortable>
          <template slot-scope="{ row }">

            <div
              style="width: 100%"
              class="el-text el-text--caption">

              {{ $t('wre-dashboard.sub-accounts-dialog-box.label.used-space', spaceUsageInfo(row.space)) }}

              <el-progress
                class="mt-8"
                size="small"
                :percentage="wormStatus(row.space).spaceUsedPercent"
                :color="wormStatus(row.space).progressColor"
              />
            </div>

          </template>
        </el-table-column>

        <el-table-column
          prop="lastVersion"
          :label="$t('wre-dashboard.sub-accounts-dialog-box.table.column.version')"
          :sort-by="row => new Date(row.backUpDate).getTime()"
          sortable>
          <template slot-scope="{ row }">
            {{ row.backUpDate | date }}
          </template>
        </el-table-column>

        <el-table-column
          prop="expires"
          :label="$t('wre-dashboard.sub-accounts-dialog-box.table.column.expires')"
          :sort-by="row => new Date(row.expireDate).getTime()"
          sortable>
          <template slot-scope="{ row }">
            <span v-if="!row.expired">
              {{ row.expireDate | date('medium') }}
            </span>
            <span
              style="color: #d92c33;"
              v-else>
              {{ $t('wre-dashboard.sub-accounts-dialog-box.label.expired') }}
            </span>
          </template>
        </el-table-column>

      </el-table>

      <template #footer>
        <el-button
          type="secondary"
          @click="onClose()">
          {{ $t('wre-dashboard.form.button.close') }}
        </el-button>
      </template>
    </el-dialog>

  </div>
</template>

<script>

import { createNamespacedHelpers } from 'vuex';

import {
  PENDING_STATUS
} from '../store/action-status';

import {
  FETCH_SUB_ACCOUNT_ITEMS,
  UPDATE_ACTIVE_SUB_ACCOUNT
} from '../store/action-types';

const { mapGetters, mapActions } = createNamespacedHelpers('wre-dashboard');

export default {
  name: 'SubAccountsTable',
  data() {
    return {
      searchInputValue: ''
    };
  },
  computed: {
    ...mapGetters([
      'getSubAccountItems',
      'getFetchSubAccountItemsStatus'
    ]),
    isFetchSubAccountItemsInProgress() {
      return this.getFetchSubAccountItemsStatus === PENDING_STATUS;
    },
    tableData() {
      const volumeSubscriptionName =
         this.$t('wre-dashboard.sub-accounts-dialog-box.label.volume-subscription');

      const subAccountItemList = this.getSubAccountItems;

      subAccountItemList.forEach((el) => {
        if (el.volumeSubscription === true) {
          el.displayName = volumeSubscriptionName;
        }
      });

      // for (const i in subAccountItemList) {
      //   if (subAccountItemList[i].volumeSubscription === true) {
      //     subAccountItemList[i].displayName = volumeSubscriptionName;
      //     break;
      //   }
      // }
      const searchValue = this.searchInputValue.toLowerCase();
      if (searchValue !== '') {
        return subAccountItemList.filter(function (sb) {
          return sb.displayName.toLowerCase().indexOf(searchValue) !== -1;
        });
      }

      return subAccountItemList;
    }
  },
  methods: {
    ...mapActions({
      fetchSubAccountItems: FETCH_SUB_ACCOUNT_ITEMS,
      updateActiveSubAccount: UPDATE_ACTIVE_SUB_ACCOUNT
    }),
    onTableRowClick(row, event, column) {
      this.updateActiveSubAccount({
        id: row.id,
        spaceUsed: row.space.used,
        spaceTotal: row.space.total,
        displayName: row.displayName,
        volumeSubscription: row.volumeSubscription
      });
      this.onClose();
    },
    spaceUsageInfo(space) {
      return {
        usedSpace: this.$options.filters.size(Math.max(space.used, 0)),
        totalSpace: this.$options.filters.size(Math.max(space.total, 0)),
        spaceFree: this.$options.filters.size(Math.max(space.total - space.used, 0))
      };
    },
    wormStatus(space) {
      let progressColor;

      let spaceUsedPercent = 0;
      let spaceUsedPercentFp = 0.0;

      if (space.total > 0) {
        spaceUsedPercentFp = (space.used / space.total) * 100;
        spaceUsedPercent = Math.min(Math.max(Math.floor(spaceUsedPercentFp), 0), 100);
      }

      const freeSpace = 100.0 - spaceUsedPercentFp;

      if (freeSpace <= 0.0) {
        progressColor = 'fixed-danger';
      } else if (freeSpace > 0.0 && freeSpace < 1.0) {
        progressColor = 'fixed-warning';
      } else if (freeSpace >= 1.0 && freeSpace < 10.0) {
        progressColor = 'fixed-warning';
      } else if (freeSpace >= 10.0 && freeSpace < 30.0) {
        progressColor = 'fixed-warning';
      } else if (freeSpace >= 30.0) {
        progressColor = 'fixed-success-lighter';
      }

      return {
        progressColor: progressColor,
        spaceUsedPercent: spaceUsedPercent
      };
    },
    onClose() {
      this.$emit('handleClose');
    }
  },
  mounted() {
    this.fetchSubAccountItems();
  }
};
</script>

<style lang="scss" scoped>

</style>
